import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Check,
  Info,
  AlertTriangle,
  Clock,
  MoreHorizontal,
  CheckCheck,
} from "lucide-react";
import { notificationService } from "@/services/notificationService";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import DialogBox from "@/components/Common/DialogBox";
import Pagination from "@/components/Common/Pagination";
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import useAuth from "@/hooks/stores/useAuth";
import ContactRequestChatDialog from "@/components/contactRequests/ContactRequestChatDialog";
import pusher from "@/services/pusherService";

const NOTIFICATION_CHANNEL_ROLES = ["admin", "recruiter", "candidate"];

/** After a colleague handles a shared notification, keep it disabled briefly, then drop it from this recruiter's list. */
const ACCESS_REVOKED_REMOVE_DELAY_MS = 10_000;

function bindPrivateNotificationEvent(channel, name, handler) {
  channel.bind(name, handler);
  if (!name.startsWith(".")) {
    channel.bind(`.${name}`, handler);
  }
}

/**
 * User id who marked the notification read (backend should include this for shared recruiter inboxes).
 * Without this, other recruiters cannot tell "colleague read it" from the list payload alone.
 */
function resolveNotificationReaderId(n) {
  if (!n || typeof n !== "object") return null;
  const raw =
    n.updated_by ??
    n.updated_by_user_id ??
    n.read_by_user_id ??
    n.reader_user_id ??
    n.read_by ??
    n.user_id_read_by ??
    n.metadata?.updated_by ??
    n.metadata?.read_by_user_id ??
    null;
  if (raw == null || raw === "") return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

/** Admin/recruiter: row is locked for you when someone else already read it. */
function deriveAccessRevokedForTeam(n, currentUserId, roleLower) {
  if (!["admin", "recruiter"].includes(roleLower)) return false;
  const read = n.read === true || n.status === "read";
  if (!read) return false;
  const reader = resolveNotificationReaderId(n);
  if (reader == null) return false;
  const uid = currentUserId != null ? Number(currentUserId) : NaN;
  if (Number.isNaN(uid)) return false;
  return reader !== uid;
}

function isContactRequestNotification(notification) {
  if (!notification) return false;
  if (notification.actionable_type?.includes?.("ContactRequest")) return true;
  const msg = (notification.message || "").toLowerCase();
  return (
    msg.includes("contact request") &&
    (msg.includes("new message") || msg.includes("message regarding"))
  );
}

function recruiterUserIdFromNotification(notification) {
  if (!notification) return null;
  const sb = notification.sent_by;
  const md = notification.metadata || {};
  const act = notification.actionable || {};
  const candidates = [
    sb?.id,
    sb?.user_id,
    md.sender_id,
    md.sender_user_id,
    md.user_id,
    act.sender_id,
    act.recruiter_user_id,
  ];
  for (const v of candidates) {
    if (v != null && v !== "") {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

/** Newest activity on top: updated_at (e.g. new messages), else created_at. */
function sortNotificationsForDisplay(list) {
  const t = (n) =>
    new Date(n.updated_at || n.created_at || 0).getTime();
  return [...list].sort((a, b) => t(b) - t(a));
}

function buildJobContextFromNotification(n) {
  if (!n) return null;
  const a = n.actionable || {};
  const md = n.metadata || {};
  const vacancy = a.vacancy || md.vacancy;
  return {
    vacancyTitle: vacancy?.title || md.vacancy_title || a.vacancy_title || "",
    companyName: vacancy?.company?.name || md.company_name || "",
    campaignName:
      vacancy?.campaign?.name || md.campaign_name || a.campaign_id || "",
    vacancyStage:
      a.vacancy_status?.status?.name ||
      md.stage_name ||
      md.status ||
      "",
  };
}

/** e.g. "Your interview slot has been confirmed." — dismiss only, no booking navigation */
function isCandidateInterviewSlotConfirmedNotification(notification) {
  const msg = (notification?.message || "").toLowerCase();
  return (
    msg.includes("interview slot") &&
    (msg.includes("confirmed") || msg.includes("has been confirm"))
  );
}

/** e.g. "Your interview slot has been cancelled." — open candidate application */
function isCandidateInterviewSlotCancelledNotification(notification) {
  const msg = (notification?.message || "").toLowerCase();
  return (
    msg.includes("interview slot") &&
    (msg.includes("cancelled") ||
      msg.includes("canceled") ||
      msg.includes("has been cancel"))
  );
}

const Notifications = () => {
  const navigate = useNavigate();
  const { userID, isAuthenticated, userRole } = useAuth();
  const roleLower = userRole?.toLowerCase?.() ?? "";
  const { showToast } = useToastStore;
  const { setHasUnreadNotifications } = useGlobalStore();
  const { getPerPage, setPerPage } = usePaginationStore();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [contactChatOpen, setContactChatOpen] = useState(false);
  const [contactChatConfig, setContactChatConfig] = useState(null);
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: getPerPage("notifications-table"),
  });

  const serverPaginationRef = useRef(serverPagination);
  serverPaginationRef.current = serverPagination;

  const openNotificationIdRef = useRef(null);
  useEffect(() => {
    openNotificationIdRef.current =
      isDialogOpen && selectedNotification?.id != null
        ? Number(selectedNotification.id)
        : null;
  }, [isDialogOpen, selectedNotification]);

  const fetchNotificationsRef = useRef(null);
  const revokedRemovalTimersRef = useRef(new Map());

  // Hide red dot when user lands on notifications page (bell clicked / visited)
  useEffect(() => {
    setHasUnreadNotifications(false);
  }, [setHasUnreadNotifications]);

  // Hide red dot when all are read (only set false here so dot stays hidden while on this page)
  useEffect(() => {
    const hasUnread = notifications.some(
      (n) => !n.read || n.status === "unread",
    );
    if (!hasUnread) setHasUnreadNotifications(false);
  }, [notifications, setHasUnreadNotifications]);

  useEffect(() => {
    if (!["admin", "recruiter"].includes(roleLower)) return;

    const revokedIds = new Set(
      notifications
        .filter((n) => n.accessRevoked === true)
        .map((n) => Number(n.id)),
    );

    for (const id of revokedIds) {
      if (revokedRemovalTimersRef.current.has(id)) continue;
      const tid = window.setTimeout(() => {
        revokedRemovalTimersRef.current.delete(id);
        setNotifications((prev) => prev.filter((n) => Number(n.id) !== id));
        setSelectedNotification((s) =>
          s && Number(s.id) === id ? null : s,
        );
        setIsDialogOpen((open) => {
          if (!open) return false;
          if (openNotificationIdRef.current === id) return false;
          return open;
        });
      }, ACCESS_REVOKED_REMOVE_DELAY_MS);
      revokedRemovalTimersRef.current.set(id, tid);
    }

    for (const [id, tid] of [
      ...revokedRemovalTimersRef.current.entries(),
    ]) {
      if (!revokedIds.has(id)) {
        window.clearTimeout(tid);
        revokedRemovalTimersRef.current.delete(id);
      }
    }
  }, [notifications, roleLower]);

  useEffect(() => {
    return () => {
      revokedRemovalTimersRef.current.forEach((tid) =>
        window.clearTimeout(tid),
      );
      revokedRemovalTimersRef.current.clear();
    };
  }, []);

  const sortedNotifications = useMemo(
    () => sortNotificationsForDisplay(notifications),
    [notifications],
  );

  const fetchNotifications = async (
    page = 1,
    per_page = 50,
    { silent = false } = {},
  ) => {
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const data = await notificationService.getNotifications({
        page,
        per_page,
      });
      const notificationsList = Array.isArray(data) ? data : data.data || [];

      // If the response follows Laravel's standard pagination structure
      if (data.pagination) {
        setServerPagination((prev) => ({ ...prev, ...data.pagination }));
      } else if (data.meta && data.links) {
        // Alternatively for JSON:API
        setServerPagination({
          current_page: data.meta.current_page,
          last_page: data.meta.last_page,
          total: data.meta.total,
          per_page: data.meta.per_page,
        });
      }

      // Normalize: accessRevoked only when API says who read it and it was not you
      const normalizedNotifications = notificationsList.map((n) => {
        const base = {
          ...n,
          read: n.read === true || n.status === "read",
        };
        return {
          ...base,
          accessRevoked: deriveAccessRevokedForTeam(base, userID, roleLower),
        };
      });

      setNotifications(normalizedNotifications);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      if (!silent) {
        setError(err.message);
        showToast({ message: err.message, type: "error" });
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  fetchNotificationsRef.current = fetchNotifications;

  useEffect(() => {
    fetchNotificationsRef.current?.(
      serverPagination.current_page,
      serverPagination.per_page,
    );
  }, [serverPagination.current_page, serverPagination.per_page]);

  /**
   * Pusher channel is per logged-in user: colleague reads do not reach your socket unless
   * the backend broadcasts to a shared channel OR you refetch. Poll + focus refetch picks up
   * `updated_by` (or similar) from GET /notifications.
   */
  useEffect(() => {
    if (!isAuthenticated || userID == null) return;
    if (!["admin", "recruiter"].includes(roleLower)) return;

    const silentRefresh = () => {
      const p = serverPaginationRef.current;
      fetchNotificationsRef.current?.(
        p.current_page,
        p.per_page,
        { silent: true },
      );
    };

    const intervalMs = 20000;
    const id = window.setInterval(silentRefresh, intervalMs);

    const onVisibleOrFocus = () => {
      if (document.visibilityState === "visible") silentRefresh();
    };
    document.addEventListener("visibilitychange", onVisibleOrFocus);
    window.addEventListener("focus", onVisibleOrFocus);

    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibleOrFocus);
      window.removeEventListener("focus", onVisibleOrFocus);
    };
  }, [isAuthenticated, userID, roleLower]);

  /** Echo: private notifications.{userId} → Pusher private-notifications.{userId} — bind only; never unsubscribe (Sidebar also uses this channel). */
  useEffect(() => {
    if (!isAuthenticated || userID == null) return;
    if (!NOTIFICATION_CHANNEL_ROLES.includes(roleLower)) return;

    const channelName = `private-notifications.${userID}`;
    const channel = pusher.subscribe(channelName);
    const isRecruiterTeam = ["admin", "recruiter"].includes(roleLower);

    const onUpdated = (payload) => {
      const rawId = payload?.id ?? payload?.notification_id;
      if (rawId == null) return;
      const id = Number(rawId);
      const status = payload?.status;
      const readerRaw =
        payload.updated_by ??
        payload.updated_by_user_id ??
        payload.read_by_user_id ??
        payload.user_id ??
        null;
      const readerNum =
        readerRaw != null && readerRaw !== ""
          ? Number(readerRaw)
          : null;
      const readerForRow =
        readerNum != null && !Number.isNaN(readerNum) ? readerNum : null;

      const patchRow = (row) => {
        if (!row || Number(row.id) !== id) return row;
        const next = { ...row };
        if (status != null) next.status = status;
        if (status === "read") {
          next.read = true;
          if (readerForRow != null) next.updated_by = readerForRow;
        }
        if (isRecruiterTeam) {
          next.accessRevoked = deriveAccessRevokedForTeam(next, userID, roleLower);
        }
        return next;
      };

      setNotifications((prev) => prev.map((n) => patchRow(n)));

      setSelectedNotification((s) => patchRow(s));

      const myUid = userID != null ? Number(userID) : NaN;
      const someoneElseRead =
        isRecruiterTeam &&
        status === "read" &&
        readerForRow != null &&
        !Number.isNaN(myUid) &&
        readerForRow !== myUid;

      if (someoneElseRead && openNotificationIdRef.current === id) {
        setIsDialogOpen(false);
      }

      void useGlobalStore.getState().refreshNotificationBellFromServer();
    };

    const onAllRead = () => {
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          read: true,
          status: "read",
          accessRevoked: false,
        })),
      );
      useGlobalStore.getState().setHasUnreadNotifications(false);
      setIsDialogOpen(false);
      setSelectedNotification(null);
    };

    const onSent = () => {
      const p = serverPaginationRef.current;
      const fn = fetchNotificationsRef.current;
      if (fn)
        void fn(p.current_page, p.per_page, { silent: true });
    };

    bindPrivateNotificationEvent(channel, "notification.updated", onUpdated);
    bindPrivateNotificationEvent(channel, "notifications.all-read", onAllRead);
    bindPrivateNotificationEvent(channel, "notification.sent", onSent);

    return () => {
      channel.unbind("notification.updated", onUpdated);
      channel.unbind(".notification.updated", onUpdated);
      channel.unbind("notifications.all-read", onAllRead);
      channel.unbind(".notifications.all-read", onAllRead);
      channel.unbind("notification.sent", onSent);
      channel.unbind(".notification.sent", onSent);
    };
  }, [isAuthenticated, userID, roleLower]);

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      const uid = userID != null ? Number(userID) : null;
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          read: true,
          status: "read",
          ...(uid != null && !Number.isNaN(uid)
            ? { updated_by: uid, accessRevoked: false }
            : {}),
        })),
      );
      setHasUnreadNotifications(false); // hide red dot on bell immediately
      showToast({
        message: "All notifications marked as read",
        type: "success",
      });
    } catch (err) {
      showToast({ message: err.message, type: "error" });
    }
  };

  const markAsRead = async (id) => {
    // Find if it's already read to avoid unnecessary API call
    const notification = notifications.find((n) => n.id === id);
    if (notification && (notification.read || notification.status === "read"))
      return;

    try {
      await notificationService.markAsRead(id);
      const uid = userID != null ? Number(userID) : null;
      setNotifications((prev) => {
        const next = prev.map((n) =>
          n.id === id
            ? {
                ...n,
                read: true,
                status: "read",
                ...(uid != null && !Number.isNaN(uid)
                  ? { updated_by: uid, accessRevoked: false }
                  : {}),
              }
            : n,
        );
        const stillHasUnread = next.some(
          (n) => !n.read || n.status === "unread",
        );
        if (!stillHasUnread) setHasUnreadNotifications(false); // hide red dot when last one read
        return next;
      });
    } catch (err) {
      showToast({ message: err.message, type: "error" });
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification?.accessRevoked) {
      showToast({
        message:
          "This notification is no longer available — it was already handled.",
        type: "info",
      });
      return;
    }
    setSelectedNotification(notification);
    setIsDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-card">
        <Loader bgColor="white" loadingText="Loading notifications..." />
      </div>
    );
  }

  const isCandidateUser = userRole?.toLowerCase?.() === "candidate";
  const showCloseOnlyForSlotConfirmed =
    isCandidateUser &&
    isCandidateInterviewSlotConfirmedNotification(selectedNotification);
  const showViewApplicationForSlotCancelled =
    isCandidateUser &&
    isCandidateInterviewSlotCancelledNotification(selectedNotification);

  return (
    <div className="py-8 w-[95%] lg:w-[80%] mx-auto pb-10">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Stay updated with your latest activities
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-2 px-4 py-2 bg-primary-dark hover:bg-primary-dark text-white font-medium rounded-lg border border-border transition-colors text-sm"
          >
            <CheckCheck size={16} />
            Mark all as read
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 text-sm flex items-center gap-3">
          <AlertTriangle size={18} />
          {error}
          <button
            onClick={fetchNotifications}
            className="ml-auto font-bold underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Notifications List */}
      <div className="space-y-3">
        {sortedNotifications.length > 0 ? (
          sortedNotifications.map((notification) => (
            <div
              key={notification.id}
              role="button"
              tabIndex={notification.accessRevoked ? -1 : 0}
              aria-disabled={notification.accessRevoked ? true : undefined}
              onClick={() => handleNotificationClick(notification)}
              onKeyDown={(e) => {
                if (
                  notification.accessRevoked ||
                  (e.key !== "Enter" && e.key !== " ")
                )
                  return;
                e.preventDefault();
                handleNotificationClick(notification);
              }}
              className={`group p-3 rounded-xl border transition-all flex items-center gap-4 ${
                notification.accessRevoked
                  ? "cursor-not-allowed opacity-60 pointer-events-none bg-muted border-border"
                  : `cursor-pointer ${
                      notification.read
                        ? "bg-card border-border hover:border-border shadow-sm hover:shadow-md"
                        : "bg-muted border-border shadow-sm hover:shadow-md"
                    }`
              }`}
            >
              {/* Type Icon */}
              <div
                className={`p-2 rounded-lg flex-shrink-0 ${
                  notification.type === "success"
                    ? "bg-emerald-50 text-emerald-600"
                    : notification.type === "warning"
                      ? "bg-amber-50 text-amber-600"
                      : "bg-primary-dark text-white"
                }`}
              >
                {notification.type === "success" ? (
                  <Check size={18} />
                ) : notification.type === "warning" ? (
                  <AlertTriangle size={18} />
                ) : (
                  <Info size={18} />
                )}
              </div>

              {/* Content - Sender Info */}
              <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
                <div className="flex flex-col min-w-0">
                  <h3
                    className={`font-semibold text-[15px] whitespace-nowrap ${notification.read ? "text-foreground" : "text-foreground"}`}
                  >
                    {notification.sent_by?.name || "System"}
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">
                    {notification.sent_by?.email || ""}
                  </p>
                </div>
                <div className="flex items-start gap-1.5 text-muted-foreground text-[11px] font-bold uppercase flex-shrink-0">
                  <Clock size={11} className="mt-0.5 flex-shrink-0" />
                  <div className="flex flex-col leading-tight">
                    {notification.updated_at
                      ? new Date(notification.updated_at).toLocaleDateString()
                      : ""}
                    {notification.updated_at && (
                      <span className="text-sm font-normal normal-case text-muted-foreground mt-0.5">
                        {new Date(notification.updated_at).toLocaleTimeString(
                          [],
                          { hour: "2-digit", minute: "2-digit", hour12: true },
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Unread Indicator */}
              {!notification.read && (
                <div className="h-2 w-2 bg-green-500 rounded-full flex-shrink-0"></div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-16 bg-muted rounded-xl border border-border">
            <div className="text-muted-foreground mb-2">
              <Bell size={40} className="mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              All caught up!
            </h3>
            <p className="text-muted-foreground text-sm">
              No new notifications at the moment.
            </p>
          </div>
        )}
      </div>
      {serverPagination && notifications.length > 0 && (
        <Pagination
          currentPage={serverPagination?.current_page}
          totalPages={serverPagination?.last_page}
          per_page={serverPagination?.per_page}
          onPerPageChange={(selectedOption) => {
            setPerPage("notifications-table", selectedOption.value);
            setServerPagination((prev) => ({
              ...prev,
              per_page: selectedOption.value,
              current_page: 1,
            }));
          }}
          onPageChange={(page) => {
            setServerPagination((prev) => ({ ...prev, current_page: page }));
          }}
        />
      )}

      {/* Footer Info */}
      <div className="mt-8 text-center text-muted-foreground text-xs font-medium">
        Showing {notifications.length} recent notifications
      </div>

      {/* Message Dialog */}
      <DialogBox
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Message Details"
        showIcon={false}
        actionButton={
          <button
            className={
              showCloseOnlyForSlotConfirmed
                ? "inline-flex w-full justify-center rounded-md border border-input bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-sm hover:bg-muted sm:w-auto"
                : "inline-flex w-full justify-center rounded-md bg-primary-dark hover:scale-105 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark sm:w-auto"
            }
            type="button"
            onClick={() => {
              if (selectedNotification?.accessRevoked) {
                setIsDialogOpen(false);
                return;
              }
              useGlobalStore.getState().setHasUnreadNotifications(false); // hide bell red dot when viewing request
              if (selectedNotification?.id) {
                markAsRead(selectedNotification.id);
              }
              if (showCloseOnlyForSlotConfirmed) {
                setIsDialogOpen(false);
                return;
              }
              if (showViewApplicationForSlotCancelled) {
                setIsDialogOpen(false);
                navigate("/candidate/applications");
                return;
              }
              const isContactRequest = isContactRequestNotification(
                selectedNotification,
              );
              const isCandidate =
                userRole?.toLowerCase?.() === "candidate";
              const isBookingRequest =
                selectedNotification?.actionable_type?.includes(
                  "BookingRequest",
                ) ||
                selectedNotification?.actionable_type?.includes(
                  "InterviewSlot",
                ) ||
                selectedNotification?.message
                  ?.toLowerCase()
                  .includes("booking");

              if (isContactRequest && isCandidate) {
                const crId = selectedNotification?.actionable_id;
                if (crId == null) {
                  showToast({
                    message: "Missing contact request reference for this chat.",
                    type: "error",
                  });
                  return;
                }
                const peerName =
                  selectedNotification?.sent_by?.name || "Recruiter";
                setContactChatConfig({
                  contactRequestId: Number(crId),
                  initialReceiverUserId:
                    recruiterUserIdFromNotification(selectedNotification),
                  headerTitle: `Chat with ${peerName}`,
                  headerEmail: "",
                  headerPhone: "",
                  chatFallbackNote: "",
                  jobContext: buildJobContextFromNotification(
                    selectedNotification,
                  ),
                });
                setIsDialogOpen(false);
                setContactChatOpen(true);
                return;
              }

              if (isContactRequest) {
                navigate("/contact-requests", {
                  state: {
                    autoOpenRequestId: selectedNotification?.actionable_id,
                    notificationData: selectedNotification,
                  },
                });
              } else if (isBookingRequest) {
                // Extract IDs from actionable or metadata (check multiple key variations)
                const actionable = selectedNotification.actionable || {};
                const metadata = selectedNotification.metadata || {};

                const vacancyId =
                  actionable.vacancy_id ||
                  metadata.vacancy_id ||
                  actionable.vacancy?.id ||
                  metadata.vacancy?.id;

                const statusId =
                  actionable.vacancy_status_id ||
                  metadata.vacancy_status_id ||
                  actionable.status_id ||
                  metadata.status_id ||
                  actionable.vacancy_status?.id ||
                  metadata.vacancy_status?.id;

                navigate(`/interviews-slots/${vacancyId}/${statusId}`, {
                  // state: {
                  //     autoOpenSlotId: actionable.id,
                  //     notificationData: selectedNotification
                  // }
                });
              } else {
                navigate("/interviews-slots");
              }
            }}
          >
            {showCloseOnlyForSlotConfirmed
              ? "Close"
              : showViewApplicationForSlotCancelled
                ? "View Application"
                : isContactRequestNotification(selectedNotification) &&
                    isCandidateUser
                  ? "Open Chat with User"
                  : isContactRequestNotification(selectedNotification)
                    ? "View Contact Request"
                    : "View Booking Request"}
          </button>
        }
      >
        <div className="py-4">
          {/* <p className="text-muted-foreground text-[13px] mb-4">
                        From: <span className="font-semibold text-foreground">{selectedNotification?.sent_by?.name}</span> ({selectedNotification?.sent_by?.email})
                    </p> */}
          <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap">
            {selectedNotification?.message}
          </p>
        </div>
      </DialogBox>

      {contactChatConfig ? (
        <ContactRequestChatDialog
          isOpen={contactChatOpen}
          onClose={() => {
            setContactChatOpen(false);
            setContactChatConfig(null);
          }}
          userID={userID}
          contactRequestId={contactChatConfig.contactRequestId}
          contactRequestRecord={null}
          initialReceiverUserId={contactChatConfig.initialReceiverUserId}
          chatFallbackNote={contactChatConfig.chatFallbackNote}
          headerTitle={contactChatConfig.headerTitle}
          headerEmail={contactChatConfig.headerEmail}
          headerPhone={contactChatConfig.headerPhone}
          jobContext={contactChatConfig.jobContext}
        />
      ) : null}
    </div>
  );
};

export default Notifications;
